import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { FaRegEdit } from 'react-icons/fa';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdChevronLeft, MdChevronRight, MdOutlineMiscellaneousServices } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { useReport } from 'contexts/ReportContext';
import { useService } from 'contexts/ServiceContext';
import CMenu from 'components/CMenu';
import Card from 'components/card/Card';
import CButton from 'components/CButton';
import ConfirmDialog from 'components/ConfirmDialog';
import Pagination from 'components/Pagination';

export default function ServicesOverview({ canCreate, canRead, canUpdate, canDelete }) {
  const { getAllServices, deleteService, updateServiceStatus } = useService();
  const { serviceReportPDF, serviceReportCSV } = useReport();
  const { toast } = useAuth();

  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [globalFilter, setGlobalFilter] = useState('');
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [filter, setFilter] = useState({
    search: '',
    limit: 10,
    page: 1,
  });
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (serviceToDelete) {
      try {
        await deleteService(serviceToDelete);
        setServiceToDelete(null);
        fetchServices();
      } catch (error) {
        console.error('Failed to delete service:', error);
      }
      setIsDialogOpen(false);
    }
  };

  const openDeleteDialog = (serviceId) => {
    setServiceToDelete(serviceId);
    setIsDialogOpen(true);
  };

  const handleServiceStatus = async (id, checked) => {
    try {
      await updateServiceStatus(id, { is_active: checked })
      fetchServices();
    } catch (error) {
      console.error("Failed to change Service status:", error);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => {
    const baseColumns = [columnHelper.accessor('logo', {
      header: () => <Text color="gray.400">LOGO</Text>,
      cell: (info) => (
        <Flex align="center">
          <Avatar
            h="48px"
            w="48px"
            bg="#fa8f03"
            color="white"
            icon={<MdOutlineMiscellaneousServices />}
            src={info.getValue()}
          />
        </Flex>
      ),
    }),
    columnHelper.accessor('name', {
      header: () => <Text color="gray.400">NAME</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      header: () => <Text color="gray.400">EMAIL</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('locationName', {
      header: () => <Text color="gray.400">LOCATION</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('address', {
      header: () => <Text color="gray.400">ADDRESS</Text>,
      cell: (info) => (
        <Text color={textColor} fontSize="md" fontWeight="500" className='address' title={info.getValue()}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('is_active', {
      header: () => <Text color="gray.400">ACTIVE</Text>,
      cell: (info) => (
        <Switch
          colorScheme="orange"
          isChecked={info.getValue()}
          onChange={(e) => handleServiceStatus(info.row.original.id, e.target.checked)}
        />
      ),
    }),]

    if (canUpdate || canDelete) {
      baseColumns.push(
        columnHelper.accessor('actions', {
          header: () => <Text color="gray.400">ACTIONS</Text>,
          cell: (info) => (
            <Flex>
              {canUpdate && <Icon
                as={FaRegEdit}
                h={6}
                w={6}
                mt='10px'
                me='20px'
                onClick={() => navigate(`/admin/services/edit/${info.row.original.id}`)}
                cursor='pointer'
                title="Edit"
                color='#fa8f03'
              />}
              {
                canDelete && <Icon
                  as={RiDeleteBin6Line}
                  h={6}
                  w={6}
                  mt='10px'
                  me='20px'
                  onClick={() => openDeleteDialog(info.row.original.id)}
                  cursor='pointer'
                  title="Delete"
                  color='#fa8f03'
                />
              }
            </Flex>
          ),
        }),
      );
    }
    return baseColumns;
  }, [canUpdate, canDelete]);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    fetchServices();
  }, [filter.search, filter.limit, filter.page]);

  const handleReportApi = async (type, filter, customDates = null) => {
    if (type === 'pdf') {
      const data = await serviceReportPDF({ filter, ...customDates });
      if (data && !data.error) {
        window.open(data.data, '_blank');
      }
    } else if (type === 'csv') {
      const data = await serviceReportCSV({ filter, ...customDates });
      if (data && !data.error) {
        window.open(data.data, '_blank');
      }
    }
  };

  const handleRangeSelect = (fileType, range) => {
    if (range !== 'custom') {
      handleReportApi(fileType, range);
    }
  };

  const handleCustomDateSubmit = (fileType, startDate, endDate) => {
    if (!startDate || !endDate) {
      toast({
        title: 'Custom filter requires both start date and end date. Please provide valid dates.',
        position: 'top',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    handleReportApi(fileType, 'custom', { startDate, endDate });
  };

  const fetchServices = async () => {
    setIsLoading(true);
    try {
      const data = await getAllServices({
        search: filter.search,
        limit: filter.limit,
        page: filter.page,
      });
      if (data && data.services) {
        setData(data.services);
        setTotalPages(data.totalPages || 0);
      } else {
        console.warn('No services found or data is undefined');
        setData([]);
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPageIndex,
    }));
    setFilter((prev) => ({
      ...prev,
      page: newPageIndex,
    }));
  };

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '55px' }}>
      <ConfirmDialog
        position="top"
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleDelete}
        header="Delete Confirmation"
        message="Are you sure you want to delete this service?"
      />
      {canRead &&
        <Card px="0px" minHeight="80vh">
          <Flex direction="column" w="100%" overflowX="auto">
            <Flex
              align="center"
              justify="space-between"
              w="100%"
              px="22px"
              mb="36px"
            >
              <DebouncedInput
                value={globalFilter ?? ''}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, search: value, page: 1 }))
                }
                placeholder="Search..."
              />

              <Flex gap="16px">
                {canCreate &&
                  <CButton
                    onClick={() => navigate('/admin/services/create')}
                  >
                    Add Service
                  </CButton>
                }
                <CMenu
                  label="PDF"
                  fileType="pdf"
                  onRangeSelect={handleRangeSelect}
                  onCustomDateSubmit={handleCustomDateSubmit}
                />

                <CMenu
                  label="CSV"
                  fileType="csv"
                  onRangeSelect={handleRangeSelect}
                  onCustomDateSubmit={handleCustomDateSubmit}
                />
              </Flex>
            </Flex>
            {
              isLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="10px"
                  overflow="hidden"
                  minHeight="500px"
                >
                  <Spinner color="#fa8f03" />
                </Box>
              ) : (
                <>
                  <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                    <Table variant="simple">
                      <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <Tr key={headerGroup.id} bg='#fa8f03'>
                            {headerGroup.headers.map((header) => (
                              <Th key={header.id} borderColor='#fa8f03'>
                                {header.isPlaceholder ? null : (
                                  <Flex
                                    onClick={header.column.getToggleSortingHandler()}
                                    cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                    justify="space-between"
                                    align="center"
                                    color="white"
                                  >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                  </Flex>
                                )}
                              </Th>
                            ))}
                          </Tr>
                        ))}
                      </Thead>
                      <Tbody>
                        {isLoading ? (
                          <Tr>
                            <Td colSpan={columns.length} textAlign="center" height="500px">
                              <Flex justify="center" align="center" height="100%">
                                <Spinner />
                              </Flex>
                            </Td>
                          </Tr>
                        ) : data.length > 0 ? (
                          table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </Td>
                              ))}
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                              No services found.
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                  <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                </>
              )}
          </Flex>
        </Card>
      }
    </Flex>
  );
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <SearchBar
      value={value}
      onChange={(e) => setValue(e.target.value)}
      h="44px"
      w={{ lg: '390px' }}
      borderRadius="16px"
      {...props}
    />
  );
}
